/// <reference types="@angular/localize" />

import { APP_INITIALIZER, enableProdMode, ErrorHandler, importProvidersFrom } from '@angular/core';

import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { bootstrapApplication, BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { LicenseServiceGrid, RoleTemplateServiceGrid } from '@myapp/features/admin/services';
import { ConfirmationDialogHtmlPaneService, ConfirmationDialogService, UiWindowService, UserService } from '@myapp/modules/fwcore/services';
import { NgbAlertModule, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { OKTA_CONFIG, OktaAuthGuard, OktaAuthModule, OktaCallbackComponent } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { NotificationService } from '@progress/kendo-angular-notification';
import { AppComponent } from './app/app.component';
import { FwcoreModule } from './app/modules/fwcore/fwcore.module';
import { ApplicationInsightsErrorHandler } from './app/modules/fwcore/handlers';
import { PageIsgGptRestrictedComponent, PageNoLicenseComponent, PageNotAuthorizedComponent } from './app/modules/fwcore/pages';
import { environment } from './environments/environment';
import { ErrorIntercept } from './interceptors/error.interceptor';
import { InterceptorDateHttp } from './interceptors/interceptorDateHttp';
import { LoaderInterceptor } from './interceptors/interceptorLoader';

import { DragDropModule } from '@angular/cdk/drag-drop';
import { PageApplicationsComponent } from './app/features/applications/pages/page-applications.component';
import { PageAskTheExpertsComponent } from './app/features/ask-the-experts/pages/page-ask-the-experts/PageAskTheExpertsComponent';
import { PageDatalabsComponent } from './app/features/ask-the-experts/pages/page-datalabs/page-datalabs.component';
import { PageDashboardComponent } from './app/features/dashboard/pages/page-dashboard/page-dashboard.component';
import { PageMiroComponent } from './app/features/miro/pages/page-miro/page-miro.component';
import { PageSearchComponent } from './app/features/search/pages/page-search/page-search.component';
import { PageCreateWordOfTheWeekComponent } from './app/features/wordoftheweek/pages/page-create-word-of-the-week/page-create-word-of-the-week.component';
import { PageWordOfTheWeekComponent } from './app/features/wordoftheweek/pages/page-word-of-the-week/page-word-of-the-week.component';
import { PageLoginComponent } from './app/features/workbench/pages/page-login/page-login.component';
import { PageNotFoundComponent } from './app/features/workbench/pages/page-not-found/page-not-found.component';
import { initSettingsTabs } from './app/modules/fwcore/components/user-settings/user-settings.func';
import { AuthGuard } from './guards/guard.service';
import { MiroCallBackComponent } from './app/features/miro/components/miro-callback/miro-callback.component';
import { getEnvironmentConfig } from './services/environment-config.service';
import { PageAqaSearchComponent } from './app/features/aqa-search/pages/page-aqa-search/page-aqa-search.component';

export function getBaseUrl() {
  return document.getElementsByTagName('base')[0].href;
}

const cacheBuster = new Date().getTime();

// Get okta settings before bootstrapping
fetch('/assets/configuration/appconfig.json?v=' + cacheBuster.toString(), {
  cache: 'no-store'
})
  .then(response => response.json())
  .then(config => {
    // Get environment-specific API configuration
    const envConfig = getEnvironmentConfig();

    // Ensure general section exists
    if (!config['general']) {
      config['general'] = {};
    }

    // Inject environment-specific adminApiUrl and apiKey
    config['general']['adminApiUrl'] = envConfig.adminApiUrl;
    config['general']['apiKey'] = envConfig.apiKey;

    const providersConfig = [
      { provide: 'BASE_URL', useFactory: getBaseUrl, deps: [] },
      { provide: 'CONFIG_SETTINGS', useValue: config }
    ];

    if (environment.production) {
      enableProdMode();
    }

    bootstrapApplication(AppComponent
      , {
        providers: [
          importProvidersFrom(
            BrowserModule
            , OktaAuthModule
            , NgbModule
            , NgbAlertModule
            , FwcoreModule
            , DragDropModule
            , NgIdleKeepaliveModule.forRoot()
          )
          , providersConfig
          , {
            provide: OKTA_CONFIG, useFactory: (config: 'CONFIG_SETTINGS') => {
              const oktaSettings = config[<any>'okta'];
              const oktaConfig = {
                issuer: oktaSettings[<any>'issuer']
                , redirectUri: oktaSettings[<any>'redirectUri']
                , postLogoutRedirectUri: oktaSettings[<any>'postLogoutRedirectUri']
                , clientId: oktaSettings[<any>'clientId']
                , scopes: Array.from(oktaSettings[<any>'scopes'])
                , pkce: JSON.parse(oktaSettings[<any>'pkce'])

                // Token management settings
                , tokenManager: {
                  storage: 'localStorage', // or 'sessionStorage', 'memory'
                  autoRenew: true,
                  secure: true,
                  expireEarlySeconds: 300 // Renew 5 minutes before expiration
                },

                // Services configuration
                services: {
                  autoRenew: true,
                  autoRemove: true
                }
              };
              const oktaAuth = new OktaAuth(oktaConfig);
              return { oktaAuth };
            }
            , deps: ['CONFIG_SETTINGS']
          }
          , {
            provide: HTTP_INTERCEPTORS
            , useClass: InterceptorDateHttp
            , multi: true
          }
          , {
            provide: HTTP_INTERCEPTORS
            , useClass: LoaderInterceptor
            , multi: true
          }
          , {
            provide: HTTP_INTERCEPTORS
            , useClass: ErrorIntercept
            , multi: true
          }
          , {
            provide: APP_INITIALIZER
            , useFactory: initSettingsTabs
            , deps: [UserService]
            , multi: true
          }
          , {
            provide: ErrorHandler
            , useClass: ApplicationInsightsErrorHandler
          }
          , provideHttpClient(withInterceptorsFromDi())
          , provideAnimations()
          , UiWindowService
          , NotificationService
          , ConfirmationDialogService
          , ConfirmationDialogHtmlPaneService
          , LicenseServiceGrid
          , RoleTemplateServiceGrid
          , provideRouter([
            { path: '', redirectTo: 'login', pathMatch: 'full' }
            , { path: 'home', component: PageDashboardComponent, canActivate: [OktaAuthGuard] }
            , { path: 'applications', component: PageApplicationsComponent, canActivate: [OktaAuthGuard, AuthGuard] }
            //, { path: 'smartbank', component: PageSmartBankComponent, canActivate: [OktaAuthGuard] }
            , { path: 'experts', component: PageAskTheExpertsComponent, canActivate: [OktaAuthGuard, AuthGuard] }
            , { path: 'login', component: PageLoginComponent }
            , { path: 'login/callback', component: OktaCallbackComponent }
            , { path: 'search', component: PageSearchComponent, canActivate: [OktaAuthGuard, AuthGuard] }
            , { path: 'experts/datalabs', component: PageDatalabsComponent, canActivate: [OktaAuthGuard] }
            , { path: 'miro-app', component: PageMiroComponent, canActivate: [OktaAuthGuard] }
            , {
              path: 'miro', component: MiroCallBackComponent, canActivate: [OktaAuthGuard], data: {
                breadcrumb: 'miro'
              }
            }
            , { path: 'wordoftheweek/curation', component: PageWordOfTheWeekComponent, canActivate: [OktaAuthGuard, AuthGuard] }
            , { path: 'createwordoftheweek/curation', component: PageCreateWordOfTheWeekComponent, canActivate: [OktaAuthGuard, AuthGuard] }

            , {
              path: 'admin'
              , loadChildren: () => import('./app/features/admin/admin.routes')
                .then((r => r.ADMIN_ROUTES))
            }
            , {
              path: 'ai'
              , loadChildren: () => import('./app/features/ai/hub/hub.routes')
                .then((r => r.Ai_hub_Routes))
            }
            , {
              path: 'admin/ai'
              , loadChildren: () => import('./app/features/ai/hub/hub.routes')
                .then((r => r.Admin_Ai_hub_Routes))
            }
            , {
              path: 'smartbank'
              , loadChildren: () => import('./app/features/smart-bank/smart-bank.routes')
                .then((r => r.SmartBank_Routes))
            },
            {
              path: 'admin'
              , loadChildren: () => import('./app/features/admin-dashboard/admin-dashboard.routes')
                .then((r => r.AdminDashboard_Routes))
            }
            , {
              path: 'salesanddeliveryhub'
              , loadChildren: () => import('./app/features/sales-and-delivery/sales-and-delivery.routes')
                .then((r => r.SalesAndDelivery_Routes))
            }
            , {
              path: 'assessments'
              , loadChildren: () => import('./app/features/assessment/assessment.routes')
                .then((r => r.Assessment_Routes))
            }
            , {
              path: 'ideaverse'
              , loadChildren: () => import('./app/features/idea-corner/idea-corner.routes')
                .then((r => r.IdeaCorner_Routes))
            }
            , {
              path: 'ai/isggpt'
              , loadChildren: () => import('./app/features/ai/chat-gpt/chat-gpt.routes')
                .then((r => r.ChatGpt_Routes))
            }
            , {
              path: 'aihub/gpts'
              , loadChildren: () => import('./app/features/gpts/gpt.routes')
                .then((r => r.Gpt_Routes))
            }
            , {
              path: 'mip'
              , loadChildren: () => import('./app/features/market-insights/MarketInsight.Routes')
                .then((r => r.MarketInsights_Routes))
            }
            , {
              path: 'pagebuilder'
              , loadChildren: () => import('./app/features/wte/wte.routes')
                .then((r => r.wte_Routes))
              , data: {}
            }
            , {
              path: 'page'
              , loadChildren: () => import('./app/features/wte/wte.routes')
                .then((r => r.wte_viewerRoute))
            },
            {
              path: 'search'
              , loadChildren: () => import('./app/features/ai-search/ai-search.routes')
                .then((r => r.AI_Search_Routes))
            },
            {
              path: 'aqasearch', component: PageAqaSearchComponent, canActivate: [OktaAuthGuard]
              , data: { breadcrumb: 'aqa search' }
            }
            , {
              path: 'dochub'
              , loadChildren: () => import('./app/features/doc-hub/doc-hub.routes')
                .then((r => r.docHub_Routes))
            }

            , { path: 'error/404', component: PageNotFoundComponent }
            , { path: 'error/not-authorized', component: PageNotAuthorizedComponent }
            , { path: 'error/isggpt-restricted', component: PageIsgGptRestrictedComponent }
            , { path: 'not-authorized', redirectTo: 'error/not-authorized' }
            , { path: 'no-license', component: PageNoLicenseComponent }
            , { path: '**', redirectTo: 'error/404' }
          ])
        ]
      })
      .catch(err => console.log(err));

  })
